<script setup lang="ts">
import { computed, onMounted, onUnmounted } from "vue";
import AppHeader from "./AppHeader.vue";
import AppSidebar from "./AppSidebar.vue";
import { useSidebarState } from "@/compositions/useSidebarState";
import { useI18n } from "vue-i18n";
import type { NavigationLink } from "@/components/SideNavigation";

defineProps<{
  links: NavigationLink[];
  showWorkspaceList?: boolean;
  showNewWorkspaceButton?: boolean;
}>();

const emit = defineEmits<{
  (e: "navigationLinkClick", link: NavigationLink): void;
  (e: "newWorkspaceClick"): void;
}>();

const { locale } = useI18n();
const isRtl = computed(() => locale.value === "ar");

const { isCollapsed, toggle, collapse } = useSidebarState();

const MOBILE_BREAKPOINT = 768;
let keyboardHandler: ((e: KeyboardEvent) => void) | null = null;
let resizeHandler: (() => void) | null = null;

onMounted(() => {
  keyboardHandler = (e: KeyboardEvent): void => {
    if ((e.ctrlKey || e.metaKey) && e.key === "b") {
      e.preventDefault();
      toggle();
    }
  };
  globalThis.addEventListener("keydown", keyboardHandler);

  if (globalThis.innerWidth < MOBILE_BREAKPOINT) collapse();

  resizeHandler = (): void => {
    if (globalThis.innerWidth < MOBILE_BREAKPOINT && !isCollapsed.value) collapse();
  };
  globalThis.addEventListener("resize", resizeHandler);
});

onUnmounted(() => {
  if (keyboardHandler) {
    globalThis.removeEventListener("keydown", keyboardHandler);
    keyboardHandler = null;
  }
  if (resizeHandler) {
    globalThis.removeEventListener("resize", resizeHandler);
    resizeHandler = null;
  }
});
</script>

<template>
  <div
    :class="['app-layout-v2', { 'app-layout-v2--collapsed': isCollapsed }]"
    :dir="isRtl ? 'rtl' : 'ltr'"
    data-test="app-layout-v2/root"
  >
    <app-header :links="links" />

    <app-sidebar
      :links="links"
      :show-workspace-list="showWorkspaceList"
      :show-new-workspace-button="showNewWorkspaceButton"
      @new-workspace-click="emit('newWorkspaceClick')"
    />

    <main class="app-layout-v2__content" data-test="app-layout-v2/content">
      <slot />
    </main>

    <div v-if="!isCollapsed" class="app-layout-v2__backdrop" aria-hidden="true" @click="collapse" />
  </div>
</template>

<style scoped lang="scss">
@import "@/styles/colors_mds.scss";

.app-layout-v2 {
  --sidebar-width: 248px;
  --header-height: 56px;
  --collapsed-width: 56px;
  --layout-gap: 6px;
  --layout-padding-inline: 6px;
  --layout-padding-block-end: 6px;

  display: grid;
  grid-template-rows: var(--header-height) 1fr;
  grid-template-columns: var(--sidebar-width) 1fr;
  // Gray background bleeds through as gutters between white panels
  background-color: $wrapper--mds_brand_appearance_neutral_weak_background-color;
  gap: var(--layout-gap);
  padding-inline: var(--layout-padding-inline);
  padding-block-end: var(--layout-padding-block-end);
  height: 100vh;
  transition: grid-template-columns 200ms cubic-bezier(0.4, 0, 0.2, 1);

  &--collapsed {
    --sidebar-width: var(--collapsed-width);
  }

  > :first-child {
    grid-row: 1;
    grid-column: 1 / -1;
  }

  > :nth-child(2) {
    grid-row: 2;
    grid-column: 1;
  }

  &__content {
    grid-row: 2;
    grid-column: 2;
    overflow: auto;
    height: 100%;
    background-color: $wrapper--mds_brand_appearance_neutral_default_background-color;
    border-radius: var(--mds_brand_border_large_radius, 8px);
  }

  &__backdrop {
    display: none;
  }
}

@media (max-width: 767px) {
  .app-layout-v2 {
    padding-inline: 0;
    padding-block-end: 0;
    gap: 0;
    grid-template-columns: 1fr;

    > :first-child {
      grid-column: 1;
    }

    > :nth-child(2) {
      position: fixed;
      top: var(--header-height);
      left: 0;
      width: 280px;
      height: calc(100vh - var(--header-height));
      z-index: 300;
      transform: translateX(-280px);
      transition: transform 200ms cubic-bezier(0.4, 0, 0.2, 1);
      grid-row: unset;
      grid-column: unset;
    }

    &:not(&--collapsed) > :nth-child(2) {
      transform: translateX(0);
    }

    &__content {
      grid-column: 1;
      border-radius: 0;
      position: relative;
      z-index: 1;
    }

    &__backdrop {
      display: block;
      position: fixed;
      inset: 0;
      top: var(--header-height);
      background-color: var(--mds_brand_appearance_overlay_background-color, rgba(0, 0, 0, 0.4));
      z-index: 200;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .app-layout-v2 {
    transition: none;
  }

  @media (max-width: 767px) {
    .app-layout-v2 > :nth-child(2) {
      transition: none;
    }
  }
}
</style>
